/**
 * 图集版播放策略 - 音频播放 + 图集展示
 * 播放音频的同时，按顺序展示图集中的图片和视频（全屏/非全屏一致）
 * - 播放顺序：图片优先、视频置后（同类型内按 sortOrder），减轻首屏卡在视频加载
 * - 图片：显示2.5秒后自动切换到下一项
 * - 视频：
 *   - 如果视频时长 >= 2.5秒：播放完成后立即切换到下一项
 *   - 如果视频时长 < 2.5秒：播放完成后定格在最后一帧，直到总时长达到2.5秒再切换
 * - 切歌判定（有音频时）：必须「全部媒体轮播完毕」且「音频至少自然播放完毕一次」
 * - 音频短于图集轮播时：音频循环重播直至媒体播完（循环结束不计入切歌条件）
 */
import { PlayStrategy } from './PlayStrategy.js';
import AudioController from '../AudioController.js';
import myRequest from '@/utils/myRequest';
import albumMediaCacheService from '../AlbumMediaCacheService.js';
import {
  prepareAlbumMediaItemsForPlayback,
  buildAlbumHlsReadyUrl,
  isAliyunAlbumVideo,
  isAlbumHlsReadyUrl,
  isAliyunCdnUrl
} from '@/utils/aliyunHlsPlayback.js';
import { runWhenPlayerOpenIdle, waitForPlayerOpenIdle } from '@/utils/playerOpenIdle.js';
import { startAlbumHlsBackgroundCache } from '@/utils/albumHlsBackgroundCache.js';
import { songHasMusicTag } from '@/utils/songTags.js';

export class AlbumStrategy extends PlayStrategy {
  constructor() {
    super();
    
    // 🔥 每次创建新策略时，创建新的 AudioController 实例
    // 确保状态完全隔离
    this.audioController = new AudioController();
    
    // 图集相关状态
    this.albumMediaItems = []; // 图集媒体项列表
    this.currentMediaIndex = 0; // 当前显示的媒体索引
    this.mediaSwitchTimer = null; // 媒体切换定时器
    this.albumCompleteTimer = null; // 最后一项播完后延迟切歌
    this.imageDisplayDuration = 2500; // 图片显示2.5秒（2500毫秒）
    this.minMediaDisplayDuration = 2500; // 最小媒体显示时长（2.5秒）
    this.albumCompleteDelayMs = 1000; // 最后一项结束后再延迟 1 秒切歌
    this.firstVideoLoadGraceMs = 6000; // 图集首项为视频时，等待 HLS 预缓冲 + 挂载
    this.firstVideoPlayGraceMs = 8000; // 首项视频已 canplay 后，再等实际起播的最长时间
    this.firstVideoLoadGraceTimer = null;
    this.firstVideoHasStartedPlaying = false;
    this.firstVideoIsReady = false; // 首项视频已 canplay 但可能尚未 play()
    this._firstVideoGraceRetryAfterReady = false;
    this.videoStartTime = null; // 视频开始播放的时间戳（用于计算短视频的定格时间）
    this.currentSong = null; // 当前歌曲对象（用于缓存）
    this.blobUrls = new Map(); // 存储创建的Blob URL，用于清理
    this.mediaSwitchingStarted = false; // 标记媒体切换是否已启动（避免重复启动）
    this._albumCompleted = false; // 已触发切歌，避免重复 ended
    this._mediaRoundComplete = false; // 图集媒体已完整轮播一轮
    this._audioEnded = false; // 音频已自然播放完毕
    this._awaitingAlbumComplete = false; // 双条件满足后，正在等待延迟切歌
    /** 是否处于播放页全屏（仅影响 UI，不影响切歌判定） */
    this.isAlbumFullscreen = false;
    /** 用户主动暂停（区别于音频自然结束触发的 pause） */
    this._userInitiatedPause = false;
    this.mediaLoadStatus = new Map(); // 媒体加载状态：index -> 'loading' | 'completed' | 'failed'
    this.loadedMediaIndices = new Set(); // 已加载完成的媒体索引集合
    this.audioDisabled = false; // 无音频的图集模式开关
    this.performanceTraceContext = null;
    this._perfFirstMediaUrlRequested = false;
    this._perfFirstMediaUrlResolved = false;
    this._perfFirstAlbumMediaChangedEmitted = false;
    this._playInProgress = false;
    /** 首帧 early present 互斥：全生命周期只 emit 一次 index 0 */
    this._earlyPresentDone = false;
    this._earlyPresentPromise = null;
    /** 同一 index 并发 getMediaUrl 去重 */
    this._mediaUrlInflight = new Map();
    /** 媒体切换代次：stop/load 时递增，过期定时器不再切图 */
    this._mediaSwitchGeneration = 0;
    /** 最近一次为图片安排自动切换的时间戳（防 play 后瞬间连切） */
    this._lastScheduledSwitchAt = 0;
    /** 重新拉取图集元数据/直链的 in-flight Promise（按 mediaAlbumId 去重） */
    this._freshAlbumMediaListPromise = null;
    this._freshAlbumMediaListKey = null;
    /** 当前索引媒体已切换但 URL 未就绪，等待预加载完成后补发 */
    this._awaitingMediaIndex = null;
    /** 单项视频图集：视频卡住时的媒体完成兜底计时器 */
    this._singleVideoFallbackTimer = null;
    
    // 绑定基础播放器的事件到策略事件系统
    this.bindAudioControllerEvents();
    
    console.log('🖼️ [AlbumStrategy] 图集版策略创建完成');
  }

  getNowMs() {
    if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
      return performance.now();
    }
    return Date.now();
  }

  setPerformanceTraceContext(context) {
    this.performanceTraceContext = context || null;
    this._perfFirstMediaUrlRequested = false;
    this._perfFirstMediaUrlResolved = false;
    this._perfFirstAlbumMediaChangedEmitted = false;
    this._earlyPresentDone = false;
    this._earlyPresentPromise = null;
    this.logPerformance('strategy:trace-context-attached', {
      songId: context?.songId || null
    });
  }

  getPerformanceTraceContext() {
    return this.performanceTraceContext;
  }

  logPerformance(step, extra = {}) {
    if (!this.performanceTraceContext || typeof this.performanceTraceContext.t0 !== 'number') {
      return;
    }
    const elapsed = Math.round(this.getNowMs() - this.performanceTraceContext.t0);
    console.log(`[PERF][AlbumStrategy][${this.performanceTraceContext.id}] t+${elapsed}ms ${step}`, extra);
  }

  markFirstAlbumMediaChangedEmit(index, mediaType) {
    if (this._perfFirstAlbumMediaChangedEmitted) {
      return;
    }
    this._perfFirstAlbumMediaChangedEmitted = true;
    this.logPerformance('album:first-media-changed-emitted', { index, mediaType });
  }

  markFirstMediaUrlRequest(index, mediaType) {
    if (this._perfFirstMediaUrlRequested) {
      return;
    }
    this._perfFirstMediaUrlRequested = true;
    this.logPerformance('album.first-media-url:request', { index, mediaType });
  }

  markFirstMediaUrlReady(index, source, extra = {}) {
    if (this._perfFirstMediaUrlResolved) {
      return;
    }
    this._perfFirstMediaUrlResolved = true;
    this.logPerformance('album.first-media-url:ready', { index, source, ...extra });
  }

  markFirstMediaUrlNull(index, extra = {}) {
    if (this._perfFirstMediaUrlResolved) {
      return;
    }
    this._perfFirstMediaUrlResolved = true;
    this.logPerformance('album.first-media-url:null', { index, ...extra });
  }

  /** 是否为可展示的图集媒体 URL（blob / HLS 标记 / 阿里云直链） */
  isPlayableAlbumUrl(url) {
    if (!url || typeof url !== 'string') return false;
    if (url.startsWith('blob:')) return true;
    if (isAlbumHlsReadyUrl(url)) return true;
    if (url.startsWith('http://') || url.startsWith('https://')) return true;
    return false;
  }

  /** 阿里云盘图片等媒体：API 已带直链时可在缓存完成前先展示（可能已过期，仅作最后兜底） */
  getAliyunDirectMediaUrl(mediaItem) {
    if (!mediaItem || mediaItem.storageType !== 2 || isAliyunAlbumVideo(mediaItem)) {
      return null;
    }
    const direct = mediaItem.url || mediaItem.pictureUrl || mediaItem.videoUrl;
    if (direct && (direct.startsWith('http://') || direct.startsWith('https://'))) {
      return direct;
    }
    return null;
  }

  emitAlbumMediaChanged(index, mediaItem, url) {
    this.emit('albumMediaChanged', {
      currentIndex: index,
      currentMedia: { ...mediaItem, url }
    });
    this.markFirstAlbumMediaChangedEmit(index, mediaItem?.mediaType);
  }

  /**
   * 等待指定媒体预加载完成或拿到可播放 URL
   */
  async waitForPlayableMediaUrl(mediaItem, index, maxWaitMs = 45000) {
    const started = Date.now();
    while (Date.now() - started < maxWaitMs) {
      const url = await this.getMediaUrl(mediaItem, index);
      if (this.isPlayableAlbumUrl(url)) {
        return url;
      }
      if (this.mediaLoadStatus.get(index) === 'failed') {
        break;
      }
      await new Promise(resolve => setTimeout(resolve, 150));
    }
    return null;
  }

  /**
   * 展示指定索引的媒体；成功返回 true 并已发出 albumMediaChanged
   */
  async presentMediaAtIndex(index) {
    const mediaItem = this.albumMediaItems?.[index];
    if (!mediaItem) return false;

    let url = await this.getMediaUrl(mediaItem, index);
    if (!this.isPlayableAlbumUrl(url)) {
      console.log(`🖼️ [AlbumStrategy] 媒体 ${index} 尚未就绪，等待预加载...`);
      this._awaitingMediaIndex = index;
      url = await this.waitForPlayableMediaUrl(mediaItem, index);
    }
    if (!this.isPlayableAlbumUrl(url)) {
      return false;
    }

    this._awaitingMediaIndex = null;
    this.emitAlbumMediaChanged(index, mediaItem, url);
    return true;
  }
  
  /**
   * 绑定 AudioController 事件到策略事件系统
   */
  bindAudioControllerEvents() {
    // 播放事件（调用 play() 后触发）
    this.audioController.on('play', () => {
      this.emit('play', {});
    });
    
    // 🔥 新增：正在播放事件（音频真正开始播放时触发，用户能听到声音）
    this.audioController.on('playing', () => {
      console.log('🖼️ [AlbumStrategy] 🔥 收到 AudioController 的 playing 事件，转发给策略管理器');
      this.emit('playing', {});
      console.log('🖼️ [AlbumStrategy] playing 事件已转发给策略管理器');
    });
    
    // 暂停事件
    this.audioController.on('pause', () => {
      // 双条件切歌：音频先结束时浏览器会先 pause 再 ended，不应打断媒体轮播
      if (this.isAudioNaturalEndDuringIncompleteMedia()) {
        console.log('🖼️ [AlbumStrategy] 音频自然结束 pause，媒体轮播未完成，忽略 pause');
        return;
      }
      this.emit('pause', {});
      // 暂停时停止媒体切换
      this.stopMediaSwitching();
    });
    
    // 时间更新事件
    this.audioController.on('timeupdate', (data) => {
      this.emit('timeupdate', {
        currentTime: data.currentTime,
        duration: data.duration
      });
    });
    
    // 播放结束事件
    this.audioController.on('ended', () => {
      if (this._albumCompleted) {
        console.log('🖼️ [AlbumStrategy] 图集已播完，忽略音频 ended');
        return;
      }
      if (this.shouldUseDualCompletionCriteria()) {
        const isFirstNaturalEnd = !this._audioEnded;
        if (isFirstNaturalEnd) {
          this._audioEnded = true;
          console.log('🖼️ [AlbumStrategy] 音频首次播放完毕，等待图集媒体轮播完毕');
          if (this.isSingleVideoAlbum()) {
            this.scheduleSingleVideoMediaFallback();
          }
        } else {
          console.log('🖼️ [AlbumStrategy] 音频循环结束，媒体轮播未完成，继续循环音频');
        }
        this.trySchedulePlaybackComplete();
        if (!this._mediaRoundComplete && !this._userInitiatedPause && this.mediaSwitchingStarted) {
          this.restartAudioWhileWaitingForMedia();
        }
        return;
      }
      this.emit('ended', {});
      this.stopMediaSwitching();
    });
    
    // 错误事件
    this.audioController.on('error', (data) => {
      this.emit('error', { error: data.error });
    });
    
    // 元数据加载完成事件
    this.audioController.on('loadedmetadata', (data) => {
      this.emit('loadedmetadata', { duration: data.duration });
    });
    
    // 可以播放事件
    this.audioController.on('canplay', () => {
      this.emit('canplay', {});
    });
  }
  
  /**
   * 加载歌曲
   * @param {Object} song - 歌曲对象
   * @returns {Promise<void>}
   */
  async load(song) {
    if (!song) {
      throw new Error('歌曲对象不能为空');
    }
    
    console.log('🖼️ [AlbumStrategy] 加载图集版歌曲:', song.title);
    if (!this.performanceTraceContext) {
      this.setPerformanceTraceContext({
        id: `album-${song?.id || 'unknown'}-${Date.now()}`,
        t0: this.getNowMs(),
        songId: song?.id || null,
        title: song?.title || ''
      });
    }
    this._perfFirstMediaUrlRequested = false;
    this._perfFirstMediaUrlResolved = false;
    this._perfFirstAlbumMediaChangedEmitted = false;
    this._earlyPresentDone = false;
    this._earlyPresentPromise = null;
    this._mediaUrlInflight.clear();
    this._mediaSwitchGeneration += 1;
    this.logPerformance('album.load:start', {
      songId: song?.id || null,
      mediaAlbumId: song?.mediaAlbumId || null
    });
    
    // 检查是否有 mediaAlbumId
    if (!song.mediaAlbumId) {
      throw new Error('图集版歌曲必须包含 mediaAlbumId 字段');
    }
    
    // 保存当前歌曲对象
    this.currentSong = song;

    // 判定是否存在可用音频
    const hasAudioSource = Boolean(
      song.fileInfoId ||
      song.audioUrl ||
      song.url ||
      song.streamUrl ||
      song.playUrl
    );
    this.audioDisabled = !hasAudioSource;
    if (this.audioDisabled) {
      console.log('🖼️ [AlbumStrategy] 检测到无音频图集，跳过音频加载');
    }
    
    // 🔥 加载新歌曲时重置媒体索引和切换状态
    this.currentMediaIndex = 0;
    this.firstVideoHasStartedPlaying = false;
    this.firstVideoIsReady = false;
    this._firstVideoGraceRetryAfterReady = false;
    this.clearFirstVideoLoadGraceTimer();
    this.mediaSwitchingStarted = false; // 重置切换启动标志
    this._albumCompleted = false;
    this._mediaRoundComplete = false;
    this._audioEnded = false;
    this._awaitingAlbumComplete = false;
    this.clearAlbumCompleteTimer();
    this.clearSingleVideoFallbackTimer();
    
    // 1. 加载音频（后台并行，不阻塞图集元数据与首帧展示）
    if (!this.audioDisabled) {
      let audioUrl = song.audioUrl || song.url;
      
      if (!audioUrl) {
        // 根据歌曲信息构造URL
        if (song.isExternalWithResources && song.playUrl) {
          audioUrl = song.playUrl;
        } else if (song.streamUrl) {
          audioUrl = song.streamUrl;
        } else if (song.fileSource === 'onedrive') {
          audioUrl = `${import.meta.env.VITE_APP_AUDIO_STREAM_BASE_URL}/api/music/${song.id}/onedrive-stream`;
        } else {
          audioUrl = `${import.meta.env.VITE_APP_AUDIO_STREAM_BASE_URL}/api/music/${song.id}/stream`;
        }
      }
      
      console.log('🖼️ [AlbumStrategy] 音频URL:', audioUrl);
      this.logPerformance('audio.loadSong:start', {});
      
      // 音频在后台加载；canplay 事件触发后再 play，不阻塞 load() 返回
      this.audioController.loadSong(song)
        .then(() => {
          this.logPerformance('audio.loadSong:done', {});
        })
        .catch((error) => {
          console.error('❌ [AlbumStrategy] 音频 loadSong 失败:', error);
          this.logPerformance('audio.loadSong:error', {
            message: error?.message || String(error)
          });
        });
    }
    
    // 2. 加载图集数据（元数据完成后即可返回；媒体预加载在内部异步进行）
    this.logPerformance('album.loadAlbumMedia:start', {});
    await this.loadAlbumMedia(song.mediaAlbumId);
    this.logPerformance('album.loadAlbumMedia:done', {});
    
    console.log('🖼️ [AlbumStrategy] 图集版歌曲加载完成');
    this.logPerformance('album.load:done', {});
  }
  
  /**
   * 加载图集媒体数据
   * @param {number|string} mediaAlbumId - 图集ID
   * @returns {Promise<void>}
   */
  async loadAlbumMedia(mediaAlbumId) {
    try {
      console.log('🖼️ [AlbumStrategy] 开始加载图集数据，mediaAlbumId:', mediaAlbumId);
      this.logPerformance('album.meta-request:start', {
        mediaAlbumId,
        isShareMusic: !!this.currentSong?.isShareMusic
      });
      
      let response;
      let usedMetaCache = false;
      const enableMetaCache = !this.currentSong?.isShareMusic;

      if (enableMetaCache) {
        this.logPerformance('album.meta-cache:lookup-start', { mediaAlbumId });
        const cachedMeta = await albumMediaCacheService.getCachedAlbumMeta(mediaAlbumId);
        if (Array.isArray(cachedMeta) && cachedMeta.length > 0) {
          response = cachedMeta;
          usedMetaCache = true;
          this.logPerformance('album.meta-cache:hit', {
            mediaAlbumId,
            mediaCount: cachedMeta.length
          });
        } else {
          this.logPerformance('album.meta-cache:miss', { mediaAlbumId });
        }
      }

      // 🔥 新增：检查是否是分享音乐，如果是，使用分享专用的API
      if (!usedMetaCache && this.currentSong && this.currentSong.isShareMusic) {
        // 分享音乐：使用分享专用的API，使用分享者的权限来获取图集数据
        const shareId = this.currentSong.shareId;
        const password = this.currentSong.sharePassword || ''; // 从歌曲对象中获取密码（如果有）
        const passwordParam = password ? `?password=${encodeURIComponent(password)}` : '';
        
        if (this.currentSong.isBatchShare) {
          // 批量分享：使用批量分享专用的API
          const musicId = this.currentSong.id;
          console.log('🖼️ [AlbumStrategy] 检测到批量分享音乐，使用批量分享专用API: shareId=', shareId, 'musicId=', musicId);
          response = await myRequest.get(`/api/music/batch-share/${shareId}/music/${musicId}/album-media${passwordParam}`);
        } else {
          // 单首分享：使用单首分享专用的API
          console.log('🖼️ [AlbumStrategy] 检测到单首分享音乐，使用分享专用API: shareId=', shareId);
          response = await myRequest.get(`/api/music/share/${shareId}/album-media${passwordParam}`);
        }
      } else if (!usedMetaCache) {
        // 普通音乐：使用原来的API
        response = await myRequest.get(`/api/media-album/${mediaAlbumId}/media`);
      }
      this.logPerformance('album.meta-request:done', {
        mediaAlbumId,
        source: usedMetaCache ? 'indexeddb-meta' : 'network',
        responseType: Array.isArray(response) ? 'array' : typeof response,
        mediaCount: Array.isArray(response) ? response.length : 0
      });
      
      // 🔥 修复：myRequest 的响应拦截器已经返回了 res.data，所以 response 直接就是数组
      // 后端返回格式：{ code: 0, data: [...] }，但响应拦截器已经提取了 data 字段
      if (response && Array.isArray(response)) {
        // 图片优先、视频置后（同类型内仍按 sortOrder），避免首项卡在视频加载
        this.albumMediaItems = prepareAlbumMediaItemsForPlayback(
          response.filter(item => item.mediaType !== undefined)
        );
        const imageCount = this.albumMediaItems.filter((i) => Number(i.mediaType) !== 1).length;
        const videoCount = this.albumMediaItems.length - imageCount;
        if (videoCount > 0) {
          console.log(
            `🖼️ [AlbumStrategy] 播放顺序已调整为图片优先（图 ${imageCount} / 视频 ${videoCount}）`
          );
        }

        if (!usedMetaCache && !this.currentSong?.isShareMusic && this.albumMediaItems.length > 0) {
          this.logPerformance('album.meta-cache:save-start', {
            mediaAlbumId,
            mediaCount: this.albumMediaItems.length
          });
          albumMediaCacheService.cacheAlbumMeta(mediaAlbumId, this.albumMediaItems).then(() => {
            this.logPerformance('album.meta-cache:save-done', { mediaAlbumId });
          }).catch(error => {
            this.logPerformance('album.meta-cache:save-error', {
              mediaAlbumId,
              message: error?.message || String(error)
            });
          });
        }
        
        console.log('🖼️ [AlbumStrategy] 图集数据加载成功，共', this.albumMediaItems.length, '项');
        console.log('🖼️ [AlbumStrategy] 图集媒体项详情:', this.albumMediaItems);
        
        // 🔥 新增：立即预加载所有图集媒体资源
        if (this.albumMediaItems.length > 0 && this.currentSong) {
          const userId = this.getCurrentUserId();
          
          // 🔥 修复：对于分享音乐，即使有直链URL，也应该进行预加载以缓存到本地
          if (this.currentSong.isShareMusic) {
            console.log('🖼️ [AlbumStrategy] 分享音乐场景，检查媒体项直链URL');
            let directUrlCount = 0;
            this.albumMediaItems.forEach((item, index) => {
              // 检查是否有完整的直链URL（http://或https://开头）
              if (item.url && (item.url.startsWith('http://') || item.url.startsWith('https://'))) {
                directUrlCount++;
                console.log(`🖼️ [AlbumStrategy] 媒体 ${index} 已有直链URL`);
              }
            });
            console.log(`🖼️ [AlbumStrategy] 分享音乐场景，共 ${directUrlCount} 个媒体项有直链URL`);
            
            // 🔥 修复：即使所有媒体都有直链URL，也应该进行预加载以缓存到本地
            // 使用 shareId 作为 userId 的替代，格式：share_{shareId}
            const shareId = this.currentSong.shareId;
            const cacheUserId = userId || (shareId ? `share_${shareId}` : null);
            
            if (cacheUserId) {
              console.log('🖼️ [AlbumStrategy] 开始预加载所有媒体资源（分享场景，即使有直链URL也进行缓存）');
              console.log(`🖼️ [AlbumStrategy] 使用缓存用户ID: ${cacheUserId}, 歌曲ID: ${this.currentSong.id}`);
              this.preloadAlbumMedia(cacheUserId, this.currentSong.id, this.albumMediaItems).catch(error => {
                console.error('❌ [AlbumStrategy] 预加载图集媒体资源失败:', error);
              });
            } else {
              console.warn('⚠️ [AlbumStrategy] 无法获取用户ID或分享ID，跳过预加载');
            }
          } else if (userId) {
            // 普通音乐：正常预加载
            console.log('🖼️ [AlbumStrategy] 开始预加载图集媒体资源，用户ID:', userId, '歌曲ID:', this.currentSong.id);
            // 异步预加载，不阻塞主流程
            this.preloadAlbumMedia(userId, this.currentSong.id, this.albumMediaItems).catch(error => {
              console.error('❌ [AlbumStrategy] 预加载图集媒体资源失败:', error);
            });
            startAlbumHlsBackgroundCache(this.albumMediaItems, this.currentSong.id);
          } else {
            console.warn('⚠️ [AlbumStrategy] 无法获取用户ID，跳过预加载');
          }
        }
        
        // 触发事件，通知组件更新图集数据
        this.emit('albumMediaLoaded', {
          mediaItems: this.albumMediaItems
        });
        this.logPerformance('album.media-loaded:emitted', {
          mediaCount: this.albumMediaItems.length
        });

        // 首帧展示由 preloadAlbumMedia 在首项预加载完成后统一触发（tryPresentFirstMediaEarly 互斥）

        // 🔥 修复：只有在 play() 方法已经调用（音频正在播放）但数据还没加载完成的情况下才自动启动
        // 如果 play() 还没调用，则由 play() 方法来启动，避免重复启动
        // 注意：加载新数据时应该重置索引，因为这是新的图集数据
        if (this.isPlaying() && !this._playInProgress && !this.mediaSwitchingStarted) {
          // 如果正在播放但切换还没启动，说明是异步加载完成的情况，需要启动
          console.log('🖼️ [AlbumStrategy] 数据加载完成时正在播放，自动启动媒体切换');
          this.startMediaSwitching(true); // 加载新数据时重置索引
        } else if (this.isPlaying() && !this._playInProgress && this.mediaSwitchingStarted) {
          // 如果已经在播放且切换已启动，说明是恢复播放的情况，需要重新启动切换（重置索引）
          console.log('🖼️ [AlbumStrategy] 数据加载完成时正在播放，重新启动媒体切换（恢复）');
          this.startMediaSwitching(true); // 加载新数据时重置索引
        }
      } else {
        console.warn('⚠️ [AlbumStrategy] 图集数据加载失败，使用空数组', {
          response,
          isArray: Array.isArray(response),
          dataType: typeof response
        });
        this.albumMediaItems = [];
      }
    } catch (error) {
      console.error('❌ [AlbumStrategy] 加载图集数据失败:', error);
      this.logPerformance('album.meta-request:error', {
        mediaAlbumId,
        message: error?.message || String(error)
      });
      this.albumMediaItems = [];
    }
  }
  
  /**
   * 播放
   * @returns {Promise<void>}
   */
  async play() {
    if (this._playInProgress) {
      this.logPerformance('album.play:dedup-hit', {});
      return;
    }
    this._playInProgress = true;
    try {
    console.log('🖼️ [AlbumStrategy] 开始播放');
    this._userInitiatedPause = false;
    this._albumCompleted = false;
    this._mediaRoundComplete = false;
    this._audioEnded = false;
    this.logPerformance('album.play:entered', {
      mediaCount: this.albumMediaItems?.length || 0,
      audioDisabled: this.audioDisabled
    });
    
    // 🔥 修复：如果图集数据还没加载完成，等待加载完成后再播放
    // 这避免了音频播放零点几秒后又从头开始播放的问题
    if (!this.albumMediaItems || this.albumMediaItems.length === 0) {
      console.log('🖼️ [AlbumStrategy] 图集数据未加载完成，等待加载完成后再播放');
      // 等待图集数据加载完成（最多等待5秒）
      const maxWaitTime = 5000;
      const startTime = Date.now();
      this.logPerformance('album.play:wait-media-start', {
        maxWaitMs: maxWaitTime
      });
      while ((!this.albumMediaItems || this.albumMediaItems.length === 0) && (Date.now() - startTime) < maxWaitTime) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      if (!this.albumMediaItems || this.albumMediaItems.length === 0) {
        console.warn('⚠️ [AlbumStrategy] 等待图集数据加载超时，继续播放');
        this.logPerformance('album.play:wait-media-timeout', {
          waitedMs: Date.now() - startTime
        });
      } else {
        console.log('🖼️ [AlbumStrategy] 图集数据加载完成，开始播放');
        this.logPerformance('album.play:wait-media-done', {
          waitedMs: Date.now() - startTime,
          mediaCount: this.albumMediaItems.length
        });
      }
    }

    // 无音频模式：直接启动媒体展示，不调用音频控制器
    if (this.audioDisabled) {
      console.log('🖼️ [AlbumStrategy] 无音频模式，直接展示图集媒体');
      this.logPerformance('album.play:audio-disabled', {
        mediaCount: this.albumMediaItems?.length || 0
      });
      if (this.albumMediaItems && this.albumMediaItems.length > 0) {
        // 先尝试加载第一个媒体并触发事件，确保界面立即展示
        try {
          const firstMedia = this.albumMediaItems[0];
          const url = await this.getMediaUrl(firstMedia, 0);
          if (url) {
            this.emit('albumMediaChanged', {
              currentIndex: 0,
              currentMedia: {
                ...firstMedia,
                url
              }
            });
            this.markFirstAlbumMediaChangedEmit(0, firstMedia?.mediaType);
          }
        } catch (e) {
          console.warn('⚠️ [AlbumStrategy] 无音频模式获取首个媒体URL失败，继续展示', e);
        }
        this.startMediaSwitching(true);
      }
      return;
    }
    
    // 预加载是后台异步进行的，不应该阻塞音频播放
    // 只要首个媒体已加载完成，即可开始媒体切换
    if (this.areFirstMediaLoaded(1)) {
      console.log('🖼️ [AlbumStrategy] 首个媒体已加载完成，可以立即开始媒体切换');
    } else {
      console.log('🖼️ [AlbumStrategy] 首个媒体未加载完成，音频先播放，媒体切换将在加载完成后开始');
    }
    
    this.logPerformance('audio.play:start', {});
    await this.audioController.play();
    this.logPerformance('audio.play:resolved', {
      currentTime: this.audioController?.getCurrentTime?.() ?? 0
    });
    
    // 🔥 修复：检查是否从头开始播放（单曲循环重新播放的情况）
    // 首次 play 时 currentTime 必然≈0，不能当成「单曲循环重启」——否则会 resetIndex
    // 把用户已滑到的项打回首项，并重挂 HLS（全视频图集首项黑屏卡住，见 RPT-900776071350）
    const currentTime = this.audioController?.getCurrentTime?.() ?? 0;
    const wasMediaSwitchingStarted = this.mediaSwitchingStarted;
    const isRestarting = wasMediaSwitchingStarted && currentTime < 0.5;
    
    // 仅「曾启动过轮播 + 定时器被 pause 清掉」才算暂停恢复；首次 play 时 timer 也为 null（RPT-6f8aa54f0057）
    const isTimerStopped = this.mediaSwitchTimer === null;
    const isPauseResume = wasMediaSwitchingStarted && isTimerStopped && !isRestarting;

    // 清除可能残留的切换定时器（避免 play 后瞬间连切）
    this.stopMediaSwitching();
    
    // 只有首个媒体已加载完成时，才立即启动媒体切换
    const canStartMediaSwitching = this.areFirstMediaLoaded(1);
    
    // 🔥 修复：如果媒体切换已经在 loadAlbumMedia 中启动（异步加载完成的情况），则不再重复启动
    // 但如果是从头开始播放（单曲循环），或者暂停后恢复，需要重新启动媒体切换
    // 但前提是前2个媒体已加载完成，否则等待加载完成后再启动
    if ((!this.mediaSwitchingStarted || isRestarting || isPauseResume) && canStartMediaSwitching) {
      // 🔥 修复：暂停后恢复时，从当前索引继续，不重置索引
      // 如果是重新播放（从头开始），应该重置索引
      // 只有在首次播放（索引无效）时才重置
      const shouldResetIndex = isRestarting || 
                              this.currentMediaIndex < 0 || 
                              this.currentMediaIndex >= (this.albumMediaItems?.length || 0);

      if (isRestarting) {
        this.currentMediaIndex = 0;
        this._mediaRoundComplete = false;
        this._audioEnded = false;
        this._awaitingAlbumComplete = false;
        this.clearAlbumCompleteTimer();
        this.clearSingleVideoFallbackTimer();
        console.log('🖼️ [AlbumStrategy] 检测到从头播放（单曲循环），重新启动媒体切换');
      } else if (isPauseResume) {
        console.log('🖼️ [AlbumStrategy] 检测到暂停后恢复，重新启动媒体切换');
      }
      
      // 🔥 修复：暂停后恢复 / 首帧已 early present 时跳过重复 emit，只恢复定时器
      const firstAlreadyPresented = this._earlyPresentDone || this._perfFirstAlbumMediaChangedEmitted;
      const skipEvent = isPauseResume
        || (isRestarting && firstAlreadyPresented && this.currentMediaIndex === 0);
      this.startMediaSwitching(shouldResetIndex, skipEvent);
      this.mediaSwitchingStarted = true;
    } else if (!canStartMediaSwitching) {
      console.log('🖼️ [AlbumStrategy] 首个媒体未加载完成，等待加载完成后再启动媒体切换');
      // 不设置 mediaSwitchingStarted，等待 statusCallback 中的逻辑来启动
    } else {
      console.log('🖼️ [AlbumStrategy] 媒体切换已启动，跳过重复启动');
    }
    
    // 🔥 自动进入全屏模式
    await this.enterFullscreenIfNeeded();

    // 音频已就绪：通知 UI 同步图集视频起播（Android 等音频未 ready 前 defer 视频 autoplay）
    this.emit('audioReadyForAlbumVideo');
    } finally {
      this._playInProgress = false;
    }
  }
  
  /**
   * 暂停
   */
  pause() {
    console.log('🖼️ [AlbumStrategy] 暂停播放');
    this._userInitiatedPause = true;
    this.audioController.pause();
    // 暂停时停止媒体切换
    this.stopMediaSwitching();
  }
  
  /**
   * 停止
   */
  stop() {
    console.log('🖼️ [AlbumStrategy] 停止播放');
    this.audioController.stop();
    // 停止时停止媒体切换并重置索引
    this.stopMediaSwitching();
    this.currentMediaIndex = 0;
  }
  
  /**
   * 设置播放时间
   * @param {number} time - 播放时间（秒）
   */
  setCurrentTime(time) {
    this.audioController.setCurrentTime(time);
  }
  
  /**
   * 获取当前播放时间
   * @returns {number}
   */
  getCurrentTime() {
    if (!this.audioController) {
      return 0;
    }
    return this.audioController.getCurrentTime();
  }
  
  /**
   * 获取总时长
   * @returns {number}
   */
  getDuration() {
    return this.audioController.getDuration();
  }
  
  /**
   * 是否正在播放
   * @returns {boolean}
   */
  isPlaying() {
    return this.audioController.isPlaying();
  }
  
  /**
   * 开始媒体切换
   * 图片：显示2.5秒后切换
   * 视频：等待视频播放完成后切换（通过 onVideoEnded 方法）
   * @param {boolean} resetIndex - 是否重置索引到第一项，默认为false（从当前索引继续）
   * @param {boolean} skipEvent - 是否跳过发送事件（用于恢复时，避免重复发送事件），默认为false
   */
  startMediaSwitching(resetIndex = false, skipEvent = false) {
    // 先停止之前的定时器
    this.stopMediaSwitching();
    
    // 🔥 修复：标记媒体切换已启动
    this.mediaSwitchingStarted = true;
    this.logPerformance('album.switching:start', {
      resetIndex,
      skipEvent,
      currentMediaIndex: this.currentMediaIndex,
      mediaCount: this.albumMediaItems?.length || 0
    });
    
    // 如果没有媒体项，不启动切换
    if (!this.albumMediaItems || this.albumMediaItems.length === 0) {
      console.warn('⚠️ [AlbumStrategy] 没有图集媒体项，无法启动切换');
      return;
    }
    
    // 🔥 修复：只有在明确要求重置或索引无效时才重置索引
    if (resetIndex || this.currentMediaIndex < 0 || this.currentMediaIndex >= this.albumMediaItems.length) {
      this.currentMediaIndex = 0;
      this._awaitingAlbumComplete = false;
      console.log('🖼️ [AlbumStrategy] 重置媒体索引到第一项');
    } else {
      console.log('🖼️ [AlbumStrategy] 从当前索引继续:', this.currentMediaIndex);
    }

    // 双条件满足后暂停打断了延迟切歌：恢复播放时重新排队
    if (
      this.shouldUseDualCompletionCriteria()
      && this._awaitingAlbumComplete
      && !resetIndex
      && this._mediaRoundComplete
      && this._audioEnded
    ) {
      console.log('🖼️ [AlbumStrategy] 恢复双条件切歌等待');
      this.scheduleAlbumComplete(this.albumCompleteDelayMs);
      return;
    }
    
    const currentMedia = this.albumMediaItems[this.currentMediaIndex];
    
    // 🔥 如果 skipEvent 为 true，说明是恢复切换，不需要重新发送事件（当前媒体已经正确显示）
    // 只需要设置定时器即可
    if (!skipEvent) {
      this.presentMediaAtIndex(this.currentMediaIndex).then((ok) => {
        if (ok) {
          this.scheduleNextSwitch(currentMedia);
        }
      }).catch(error => {
        console.error('❌ [AlbumStrategy] 展示当前媒体失败:', error);
      });
    } else {
      console.log('🖼️ [AlbumStrategy] 恢复切换，跳过发送事件，直接设置定时器');
      this.logPerformance('album.switching:skip-event', {
        currentMediaIndex: this.currentMediaIndex
      });
      this.scheduleNextSwitch(currentMedia);
    }
    
    console.log('🖼️ [AlbumStrategy] 开始媒体切换，共', this.albumMediaItems.length, '项，当前索引:', this.currentMediaIndex);
  }
  
  /**
   * 图集第一项是否为视频
   */
  isFirstAlbumItemVideo() {
    return this.albumMediaItems?.[0]?.mediaType === 1;
  }

  clearFirstVideoLoadGraceTimer() {
    if (this.firstVideoLoadGraceTimer) {
      clearTimeout(this.firstVideoLoadGraceTimer);
      this.firstVideoLoadGraceTimer = null;
    }
  }

  /**
   * 图集首项为视频且尚未开始播放时，等待 HLS 挂载；就绪后优先起播，避免误切下一项
   */
  startFirstVideoLoadGraceTimer(isRetryAfterReady = false) {
    this.clearFirstVideoLoadGraceTimer();
    if (!this.isFirstAlbumItemVideo() || this.currentMediaIndex !== 0) {
      return;
    }
    if (this.firstVideoHasStartedPlaying) {
      return;
    }
    const generation = this._mediaSwitchGeneration;
    const timeoutMs = isRetryAfterReady
      ? this.firstVideoPlayGraceMs
      : this.firstVideoLoadGraceMs;
    this._firstVideoGraceRetryAfterReady = isRetryAfterReady;
    this.firstVideoLoadGraceTimer = setTimeout(() => {
      this.firstVideoLoadGraceTimer = null;
      this._firstVideoGraceRetryAfterReady = false;
      if (generation !== this._mediaSwitchGeneration) {
        return;
      }
      if (
        this.currentMediaIndex !== 0
        || this.firstVideoHasStartedPlaying
        || !this.isFirstAlbumItemVideo()
      ) {
        return;
      }
      // 已 canplay 但 autoplay 未触发：再请求 UI 起播并延长等待，不要直接跳过首项
      if (!isRetryAfterReady && this.firstVideoIsReady) {
        console.log('🎬 [AlbumStrategy] 首项视频已就绪但未起播，请求播放并延长等待');
        this.emit('firstVideoNeedsPlay');
        this.startFirstVideoLoadGraceTimer(true);
        return;
      }
      console.log(
        `🎬 [AlbumStrategy] 首项视频${isRetryAfterReady ? '就绪后仍' : ''}未起播，超时切换下一项`
      );
      this.switchToNextMedia({ allowPremature: true });
    }, timeoutMs);
    console.log(
      `🎬 [AlbumStrategy] 首项视频${isRetryAfterReady ? '就绪后等待起播' : '尚未播放，等待加载'}（${timeoutMs / 1000}s）`
    );
  }

  /**
   * 首项视频已 canplay（由 DXHPlayer 调用），取消「加载阶段」宽限并进入「等待起播」
   */
  onFirstVideoReadyToPlay() {
    if (this.currentMediaIndex !== 0 || !this.isFirstAlbumItemVideo()) {
      return;
    }
    if (this.firstVideoHasStartedPlaying) {
      return;
    }
    this.firstVideoIsReady = true;
    this.clearFirstVideoLoadGraceTimer();
    this.emit('firstVideoNeedsPlay');
    this.startFirstVideoLoadGraceTimer(true);
  }

  /**
   * 首项视频已开始播放（由 DXHPlayer 调用），取消加载宽限计时
   */
  onFirstVideoStartedPlaying() {
    if (this.currentMediaIndex !== 0 || !this.isFirstAlbumItemVideo()) {
      return;
    }
    this.firstVideoHasStartedPlaying = true;
    this.firstVideoIsReady = true;
    this._firstVideoGraceRetryAfterReady = false;
    this.clearFirstVideoLoadGraceTimer();
  }

  /**
   * 停止媒体切换
   */
  stopMediaSwitching() {
    this._mediaSwitchGeneration += 1;
    if (this.mediaSwitchTimer) {
      clearTimeout(this.mediaSwitchTimer);
      this.mediaSwitchTimer = null;
      console.log('🖼️ [AlbumStrategy] 停止媒体切换');
    }
    this.clearFirstVideoLoadGraceTimer();
    this.clearAlbumCompleteTimer();
    // 重置视频开始时间
    this.videoStartTime = null;
    // 🔥 注意：不重置 mediaSwitchingStarted，因为停止后可能还会恢复
  }

  clearAlbumCompleteTimer() {
    if (this.albumCompleteTimer) {
      clearTimeout(this.albumCompleteTimer);
      this.albumCompleteTimer = null;
    }
  }

  clearSingleVideoFallbackTimer() {
    if (this._singleVideoFallbackTimer) {
      clearTimeout(this._singleVideoFallbackTimer);
      this._singleVideoFallbackTimer = null;
    }
  }

  /** 仅 1 项且为视频的图集（RPT-6f8aa54f0057） */
  isSingleVideoAlbum() {
    return this.albumMediaItems?.length === 1
      && this.albumMediaItems[0]?.mediaType === 1;
  }

  /**
   * 单项视频长时间未 ended：强制标记媒体轮播完成，避免音频无限循环
   */
  scheduleSingleVideoMediaFallback() {
    this.clearSingleVideoFallbackTimer();
    if (this._mediaRoundComplete || this._albumCompleted || !this.isSingleVideoAlbum()) {
      return;
    }
    const audioDurationSec = this.audioController?.getDuration?.() ?? 0;
    const delayMs = Math.max(
      15000,
      Math.ceil((Number.isFinite(audioDurationSec) ? audioDurationSec : 15) * 1000)
        + this.firstVideoPlayGraceMs
    );
    const generation = this._mediaSwitchGeneration;
    console.log(`🖼️ [AlbumStrategy] 单项视频图集启动媒体完成兜底（${delayMs / 1000}s）`);
    this._singleVideoFallbackTimer = setTimeout(() => {
      this._singleVideoFallbackTimer = null;
      if (generation !== this._mediaSwitchGeneration) {
        return;
      }
      if (this._mediaRoundComplete || this._albumCompleted || !this.isSingleVideoAlbum()) {
        return;
      }
      console.warn('🖼️ [AlbumStrategy] 单项视频图集超时未 ended，强制标记媒体轮播完成');
      this._mediaRoundComplete = true;
      this.trySchedulePlaybackComplete();
    }, delayMs);
  }

  /**
   * 首项视频 stall（由 DXHPlayer 调用）：重新请求起播并延长 grace
   */
  onAlbumVideoStalled() {
    if (this.currentMediaIndex !== 0 || !this.isFirstAlbumItemVideo()) {
      return;
    }
    if (this._mediaRoundComplete || this._albumCompleted) {
      return;
    }
    console.log('🎬 [AlbumStrategy] 首项视频 stall，请求重新起播');
    this.firstVideoHasStartedPlaying = false;
    this.firstVideoIsReady = true;
    this.clearFirstVideoLoadGraceTimer();
    this.emit('firstVideoNeedsPlay');
    this.startFirstVideoLoadGraceTimer(true);
  }

  /**
   * 音频先于图集轮播完毕时，循环重播音频（仅首次自然结束计入切歌条件）
   */
  async restartAudioWhileWaitingForMedia() {
    if (
      this._albumCompleted
      || this._userInitiatedPause
      || this._mediaRoundComplete
      || !this.mediaSwitchingStarted
      || !this.shouldUseDualCompletionCriteria()
    ) {
      return;
    }
    const ac = this.audioController;
    if (!ac?.audio) {
      return;
    }
    try {
      console.log('🖼️ [AlbumStrategy] 媒体轮播未完成，循环重播音频');
      ac.setCurrentTime(0, { userInitiated: false });
      await ac.play();
    } catch (error) {
      console.warn('⚠️ [AlbumStrategy] 等待媒体时循环音频失败:', error);
    }
  }

  /**
   * 音频先于图集轮播结束时，浏览器会先 pause 再 ended
   */
  isAudioNaturalEndDuringIncompleteMedia() {
    if (!this.shouldUseDualCompletionCriteria()) {
      return false;
    }
    if (this._mediaRoundComplete) {
      return false;
    }
    if (!this.mediaSwitchingStarted || this._userInitiatedPause) {
      return false;
    }
    const audio = this.audioController?.audio;
    if (!audio) {
      return false;
    }
    const duration = audio.duration;
    const currentTime = audio.currentTime;
    if (audio.ended) {
      return true;
    }
    return Number.isFinite(duration) && duration > 0 && currentTime >= duration - 0.3;
  }

  /**
   * 是否应继续图集媒体轮播（双条件未齐时，音频自然结束也不中断）
   */
  shouldContinueAlbumMediaSwitching() {
    if (!this.mediaSwitchingStarted) {
      return false;
    }
    if (this._userInitiatedPause || this._albumCompleted) {
      return false;
    }
    if (this.shouldUseDualCompletionCriteria()) {
      return !(this._mediaRoundComplete && this._audioEnded);
    }
    if (this.audioDisabled) {
      return true;
    }
    return this.isPlaying();
  }

  shouldUseDualCompletionCriteria() {
    if (this.audioDisabled) {
      return false;
    }
    const count = this.albumMediaItems?.length || 0;
    return count > 0;
  }

  trySchedulePlaybackComplete() {
    if (this._albumCompleted || this._userInitiatedPause) {
      return;
    }
    if (this.shouldUseDualCompletionCriteria()) {
      if (!this._mediaRoundComplete || !this._audioEnded) {
        return;
      }
    } else if (!this.audioDisabled) {
      return;
    }
    this.scheduleAlbumComplete(this.albumCompleteDelayMs);
  }

  /**
   * 最后一项媒体已展示完毕：延迟后按播放模式切歌（不再循环图集）
   */
  scheduleAlbumComplete(delayMs = this.albumCompleteDelayMs) {
    if (this.shouldUseDualCompletionCriteria() && (!this._mediaRoundComplete || !this._audioEnded)) {
      console.log('🖼️ [AlbumStrategy] 跳过切歌，等待媒体+音频双条件', {
        mediaRoundComplete: this._mediaRoundComplete,
        audioEnded: this._audioEnded
      });
      this._awaitingAlbumComplete = false;
      this.clearAlbumCompleteTimer();
      return;
    }
    this.clearAlbumCompleteTimer();
    if (this.mediaSwitchTimer) {
      clearTimeout(this.mediaSwitchTimer);
      this.mediaSwitchTimer = null;
    }
    this.clearFirstVideoLoadGraceTimer();
    this.videoStartTime = null;
    this._awaitingAlbumComplete = true;

    const generation = this._mediaSwitchGeneration;
    console.log(`🖼️ [AlbumStrategy] 图集播放完毕（媒体+音频），${delayMs}ms 后触发切歌`);
    this.albumCompleteTimer = setTimeout(() => {
      this.albumCompleteTimer = null;
      if (generation !== this._mediaSwitchGeneration) {
        return;
      }
      this.finishAlbumPlayback();
    }, delayMs);
  }

  /**
   * 图集一轮播放完成 → 走与音频 ended 相同的自动切歌链路
   * 注意：仅全屏多项图集应调用；单项有音频 / 非全屏以音频 ended 为准
   */
  finishAlbumPlayback() {
    if (this._userInitiatedPause) {
      console.log('🖼️ [AlbumStrategy] 用户已暂停，忽略图集视觉播完切歌');
      this._awaitingAlbumComplete = false;
      this.clearAlbumCompleteTimer();
      return;
    }
    if (this.shouldUseDualCompletionCriteria() && (!this._mediaRoundComplete || !this._audioEnded)) {
      console.log('🖼️ [AlbumStrategy] 忽略切歌，媒体或音频尚未完成', {
        mediaRoundComplete: this._mediaRoundComplete,
        audioEnded: this._audioEnded
      });
      this._awaitingAlbumComplete = false;
      this.clearAlbumCompleteTimer();
      return;
    }
    if (this._albumCompleted) {
      return;
    }
    this._albumCompleted = true;
    this._awaitingAlbumComplete = false;
    this.mediaSwitchingStarted = false;
    this.clearAlbumCompleteTimer();
    this._mediaSwitchGeneration += 1;

    console.log('🖼️ [AlbumStrategy] 图集全部媒体项与音频均播放完毕，触发 ended');

    if (!this.audioDisabled && this.audioController) {
      try {
        if (typeof this.audioController.pause === 'function') {
          this.audioController.pause();
        }
      } catch (error) {
        console.warn('⚠️ [AlbumStrategy] 图集播完暂停音频失败:', error);
      }
    }

    this.emit('ended', { reason: 'album-complete' });
  }
  
  /**
   * 根据媒体类型安排下一次切换
   * @param {Object} media - 当前媒体项
   * @param {{ remainingMs?: number }} options - remainingMs 用于拖动进度条后按剩余时间切下一项
   */
  scheduleNextSwitch(media, options = {}) {
    // 先清除之前的定时器
    this.stopMediaSwitching();
    
    if (!media) {
      return;
    }

    const { remainingMs } = options;
    
    // mediaType: 0-图片，1-视频
    if (media.mediaType === 0) {
      // 图片：默认 2.5 秒；拖动后可按剩余时间切换
      const delay = typeof remainingMs === 'number' && remainingMs >= 0
        ? Math.max(120, remainingMs)
        : this.imageDisplayDuration;
      this._lastScheduledSwitchAt = Date.now() - (this.imageDisplayDuration - delay);
      const generation = this._mediaSwitchGeneration;
      this.mediaSwitchTimer = setTimeout(() => {
        if (generation !== this._mediaSwitchGeneration) {
          return;
        }
        if (!this.shouldContinueAlbumMediaSwitching()) {
          return;
        }
        this.switchToNextMedia();
      }, delay);
      console.log(`🖼️ [AlbumStrategy] 图片媒体，将在 ${(delay / 1000).toFixed(2)} 秒后切换`);
    } else if (media.mediaType === 1) {
      // 视频：记录开始播放时间，等待视频播放完成事件
      // 视频的切换由 onVideoEnded 方法触发
      this.videoStartTime = Date.now();
      // 首项视频可能在 audio.play() resolve 之前就已 playing（early present）。
      // 不要在这里清掉标志并重启 6s grace，否则会误判「尚未播放」并重挂 HLS。
      if (this.currentMediaIndex === 0 && this.isFirstAlbumItemVideo()) {
        if (!this.firstVideoHasStartedPlaying) {
          this.firstVideoIsReady = false;
          this._firstVideoGraceRetryAfterReady = false;
          this.startFirstVideoLoadGraceTimer();
        }
      } else {
        this.firstVideoHasStartedPlaying = false;
        this.firstVideoIsReady = false;
        this._firstVideoGraceRetryAfterReady = false;
      }
      console.log('🎬 [AlbumStrategy] 视频媒体，等待视频播放完成');
    }
  }
  
  /**
   * 切换到下一项媒体
   */
  switchToNextMedia(options = {}) {
    const { allowPremature = false } = options;
    if (!this.albumMediaItems || this.albumMediaItems.length === 0) {
      return;
    }
    if (!this.shouldContinueAlbumMediaSwitching()) {
      console.log('🖼️ [AlbumStrategy] 跳过切换：未在播放或切换未启动');
      return;
    }
    if (
      !allowPremature
      && this._lastScheduledSwitchAt > 0
      && Date.now() - this._lastScheduledSwitchAt < this.imageDisplayDuration - 200
    ) {
      console.log('🖼️ [AlbumStrategy] 忽略过早的自动切换', {
        elapsedMs: Date.now() - this._lastScheduledSwitchAt
      });
      return;
    }

    this.stopMediaSwitching();

    // 已在最后一项
    if (this.currentMediaIndex >= this.albumMediaItems.length - 1) {
      if (this.shouldUseDualCompletionCriteria()) {
        this._mediaRoundComplete = true;
        this.clearSingleVideoFallbackTimer();
        console.log('🖼️ [AlbumStrategy] 图集媒体已轮播完毕，等待音频结束');
        if (this._audioEnded) {
          this.scheduleAlbumComplete(this.albumCompleteDelayMs);
          return;
        }
        this._awaitingAlbumComplete = false;
        this.currentMediaIndex = 0;
      } else {
        this.scheduleAlbumComplete(this.albumCompleteDelayMs);
        return;
      }
    } else {
      // 切换到下一项
      this._awaitingAlbumComplete = false;
      this.currentMediaIndex = this.currentMediaIndex + 1;
    }

    const nextMedia = this.albumMediaItems[this.currentMediaIndex];
    const index = this.currentMediaIndex;
    const loopMode = this.shouldUseDualCompletionCriteria()
      ? (this._mediaRoundComplete && !this._audioEnded
        ? '(媒体循环·等音频)'
        : (this._audioEnded && !this._mediaRoundComplete ? '(音频循环·等媒体)' : '(双条件切歌)'))
      : '(无音频·媒体播完切歌)';

    console.log('🖼️ [AlbumStrategy] 切换到媒体项', index, '/', this.albumMediaItems.length,
      '类型:', nextMedia.mediaType === 0 ? '图片' : '视频',
      loopMode);

    // 仅当媒体成功展示后再 scheduleNextSwitch，避免「跳过展示却定时切下一项」
    this.presentMediaAtIndex(index).then((ok) => {
      if (ok) {
        console.log('🖼️ [AlbumStrategy] albumMediaChanged 事件已发出');
        this.scheduleNextSwitch(nextMedia);
      } else {
        console.warn(`⚠️ [AlbumStrategy] 媒体 ${index} 暂不可展示，等待预加载完成后自动补发`);
      }
    }).catch(error => {
      console.error('❌ [AlbumStrategy] 切换媒体失败:', error);
      this._awaitingMediaIndex = index;
    });
  }
  
  /**
   * 视频播放完成时调用（由 DXHPlayer 调用）
   * @param {number} videoDuration - 视频时长（秒），如果未提供则使用实际播放时间
   */
  onVideoEnded(videoDuration = null) {
    this.clearFirstVideoLoadGraceTimer();
    const now = Date.now();
    let actualPlayTime = null;
    
    // 如果提供了视频时长，使用提供的时长；否则使用实际播放时间
    if (videoDuration !== null && videoDuration !== undefined) {
      actualPlayTime = videoDuration * 1000; // 转换为毫秒
    } else if (this.videoStartTime !== null) {
      actualPlayTime = now - this.videoStartTime;
    }
    
    // 如果视频播放时长不足2.5秒，需要定格到2.5秒
    if (actualPlayTime !== null && actualPlayTime < this.minMediaDisplayDuration) {
      const remainingTime = this.minMediaDisplayDuration - actualPlayTime;
      console.log(`🎬 [AlbumStrategy] 视频播放完成，时长 ${(actualPlayTime / 1000).toFixed(2)}秒，不足2.5秒，将定格 ${(remainingTime / 1000).toFixed(2)}秒后切换`);
      
      // 设置定时器，在剩余时间后切换
      const generation = this._mediaSwitchGeneration;
      this.mediaSwitchTimer = setTimeout(() => {
        if (generation !== this._mediaSwitchGeneration) {
          return;
        }
        this.switchToNextMedia({ allowPremature: true });
      }, remainingTime);
      
      // 通知 DXHPlayer 需要定格视频（暂停在最后一帧）
      this.emit('videoNeedsFreeze', { remainingTime });
    } else {
      // 视频时长 >= 2.5秒，立即切换
      console.log(`🎬 [AlbumStrategy] 视频播放完成，时长 ${actualPlayTime ? (actualPlayTime / 1000).toFixed(2) : '未知'}秒，立即切换到下一项`);
      this.switchToNextMedia({ allowPremature: true });
    }
    
    // 重置视频开始时间
    this.videoStartTime = null;
  }
  
  /**
   * 获取当前媒体项
   * @returns {Object|null}
   */
  getCurrentMedia() {
    if (!this.albumMediaItems || this.albumMediaItems.length === 0) {
      return null;
    }
    return this.albumMediaItems[this.currentMediaIndex] || null;
  }
  
  /**
   * 获取所有媒体项
   * @returns {Array}
   */
  getAllMediaItems() {
    return this.albumMediaItems || [];
  }

  /**
   * 设置当前媒体索引（用于手动切换时同步索引）
   * @param {number} index - 媒体索引
   */
  setCurrentMediaIndex(index) {
    if (typeof index !== 'number' || index < 0) {
      console.warn('⚠️ [AlbumStrategy] 无效的媒体索引:', index);
      return;
    }
    
    if (!this.albumMediaItems || index >= this.albumMediaItems.length) {
      console.warn('⚠️ [AlbumStrategy] 媒体索引超出范围:', index, '总数:', this.albumMediaItems?.length || 0);
      return;
    }
    
    this.currentMediaIndex = index;
    this._awaitingAlbumComplete = false;
    this.clearAlbumCompleteTimer();
    console.log('🖼️ [AlbumStrategy] 手动设置媒体索引:', index);
    
    // 如果正在自动切换，需要重新安排切换（从新的索引开始）
    // 但这里不自动启动切换，由调用方决定是否启动
  }

  /**
   * 🔥 缓存相关方法
   */

  /**
   * 获取当前用户ID
   * @returns {string|number|null}
   */
  getCurrentUserId() {
    try {
      const token = localStorage.getItem('accessToken');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload.sub || payload.userId || payload.id;
      }
    } catch (error) {
      console.warn('⚠️ [AlbumStrategy] 获取用户ID失败:', error);
    }
    return null;
  }

  /**
   * 首帧就绪后尽早展示图集（不等待音频 loadSong / 全量预加载）
   */
  async tryPresentFirstMediaEarly() {
    if (this._earlyPresentDone) {
      return true;
    }
    if (this._earlyPresentPromise) {
      return this._earlyPresentPromise;
    }
    if (!this.albumMediaItems || this.albumMediaItems.length === 0) {
      return false;
    }

    this._earlyPresentPromise = (async () => {
      try {
        if (this._earlyPresentDone) {
          return true;
        }
        const ok = await this.presentMediaAtIndex(0);
        if (ok) {
          this._earlyPresentDone = true;
          this.logPerformance('album.first-media-presented-early', { index: 0 });
        }
        return ok;
      } finally {
        this._earlyPresentPromise = null;
      }
    })();

    return this._earlyPresentPromise;
  }

  createThrottledPreloadProgressLogger() {
    const state = new Map();
    const minIntervalMs = 500;
    const minStep = 10;
    return (index, progress, total) => {
      if (progress >= 100) {
        console.log(`🖼️ [AlbumStrategy] 预加载进度: ${index + 1}/${total}, 100%`);
        state.delete(index);
        return;
      }
      const now = Date.now();
      const prev = state.get(index) || { t: 0, p: -1 };
      if (progress - prev.p < minStep && now - prev.t < minIntervalMs) {
        return;
      }
      state.set(index, { t: now, p: progress });
      console.log(`🖼️ [AlbumStrategy] 预加载进度: ${index + 1}/${total}, ${progress}%`);
    };
  }

  buildPreloadStatusCallback() {
    return (index, status) => {
      this.mediaLoadStatus.set(index, status);
      if (status === 'completed') {
        this.loadedMediaIndices.add(index);

        if (this.isPlaying() && this.mediaSwitchingStarted && this._awaitingMediaIndex === index) {
          this.presentMediaAtIndex(index).then((ok) => {
            if (ok && index === this.currentMediaIndex && !this.mediaSwitchTimer) {
              const media = this.albumMediaItems[index];
              if (media) {
                console.log(`🖼️ [AlbumStrategy] 媒体 ${index} 预加载完成，补发展示并安排下一次切换`);
                this.scheduleNextSwitch(media);
              }
            }
          });
        }

        if (
          this.isPlaying()
          && !this._playInProgress
          && !this.mediaSwitchingStarted
          && this.areFirstMediaLoaded(1)
        ) {
          console.log('🖼️ [AlbumStrategy] 首个媒体加载完成，自动启动媒体切换');
          this.startMediaSwitching(true);
          this.mediaSwitchingStarted = true;
        }
      }
      console.log(`🖼️ [AlbumStrategy] 媒体 ${index} 状态更新: ${status}`);
    };
  }

  handlePreloadBatchComplete(result) {
    result.loadedIndices.forEach((idx) => this.loadedMediaIndices.add(idx));

    if (
      this.isPlaying()
      && !this._playInProgress
      && !this.mediaSwitchingStarted
      && this.areFirstMediaLoaded(1)
    ) {
      console.log('🖼️ [AlbumStrategy] 预加载批次完成，首个媒体已加载，自动启动媒体切换');
      this.startMediaSwitching(true);
      this.mediaSwitchingStarted = true;
    }

    if (this.isPlaying() && this.mediaSwitchingStarted) {
      const currentMedia = this.albumMediaItems[this.currentMediaIndex];
      if (currentMedia) {
        this.getMediaUrl(currentMedia, this.currentMediaIndex)
          .then((url) => {
            if (url && (url.startsWith('blob:') || isAlbumHlsReadyUrl(url))) {
              this.emit('albumMediaChanged', {
                currentIndex: this.currentMediaIndex,
                currentMedia: {
                  ...currentMedia,
                  url
                }
              });
              this.markFirstAlbumMediaChangedEmit(this.currentMediaIndex, currentMedia?.mediaType);
              console.log('🖼️ [AlbumStrategy] 预加载批次完成，已触发当前媒体URL更新');
            }
          })
          .catch((error) => {
            console.warn('⚠️ [AlbumStrategy] 预加载批次完成后获取当前媒体URL失败:', error);
          });
      }
    }
  }

  /**
   * 预加载图集中的所有媒体资源
   * @param {string|number} userId - 用户ID
   * @param {string|number} songId - 歌曲ID
   * @param {Array} mediaItems - 媒体项列表
   * @returns {Promise<Object>} 返回加载状态对象 { loadedCount, failedCount, loadedIndices }
   */
  async preloadAlbumMedia(userId, songId, mediaItems) {
    try {
      console.log(`🖼️ [AlbumStrategy] 开始预加载 ${mediaItems.length} 个媒体资源（首项优先，其余懒加载）`);
      this.logPerformance('album.preload:start', {
        mediaCount: mediaItems?.length || 0,
        hasUserId: !!userId,
        songId,
        mode: 'priority-first'
      });

      this.mediaLoadStatus.clear();
      this.loadedMediaIndices.clear();

      const progressCallback = this.createThrottledPreloadProgressLogger();
      const statusCallback = this.buildPreloadStatusCallback();

      const priorityResult = await albumMediaCacheService.preloadAllMedia(mediaItems, userId, songId, {
        progressCallback,
        statusCallback,
        indicesToLoad: [0]
      });
      this.handlePreloadBatchComplete(priorityResult);
      this.logPerformance('album.preload:priority-done', {
        loadedCount: priorityResult.loadedCount,
        failedCount: priorityResult.failedCount
      });

      await waitForPlayerOpenIdle({ timeout: 1200, delayMs: 32 });
      await this.tryPresentFirstMediaEarly();

      if (mediaItems.length <= 1) {
        this.logPerformance('album.preload:done', {
          loadedCount: priorityResult.loadedCount,
          failedCount: priorityResult.failedCount
        });
        return priorityResult;
      }

      const restIndices = Array.from({ length: mediaItems.length - 1 }, (_, i) => i + 1);
      const startBackgroundPreload = () => {
        albumMediaCacheService.preloadAllMedia(mediaItems, userId, songId, {
          progressCallback,
          statusCallback,
          indicesToLoad: restIndices
        }).then((result) => {
          restIndices.forEach((idx) => {
            if (result.loadedIndices.has(idx)) {
              this.loadedMediaIndices.add(idx);
            }
          });
          console.log(`🖼️ [AlbumStrategy] 后台懒加载完成: 成功 ${result.loadedCount} 个, 失败 ${result.failedCount} 个`);
          this.logPerformance('album.preload:done', {
            loadedCount: priorityResult.loadedCount + result.loadedCount,
            failedCount: priorityResult.failedCount + result.failedCount,
            background: true
          });
          this.handlePreloadBatchComplete(result);
        }).catch((error) => {
          console.error('❌ [AlbumStrategy] 后台懒加载失败:', error);
          this.logPerformance('album.preload:error', {
            message: error?.message || String(error),
            phase: 'background'
          });
        });
      };
      runWhenPlayerOpenIdle(startBackgroundPreload, { timeout: 2000, delayMs: 120 });

      return priorityResult;
    } catch (error) {
      console.error('❌ [AlbumStrategy] 预加载失败:', error);
      this.logPerformance('album.preload:error', {
        message: error?.message || String(error)
      });
      return { loadedCount: 0, failedCount: 0, loadedIndices: new Set() };
    }
  }
  
  /**
   * 检查前N个媒体是否已加载完毕（用于判断是否可以开始播放）
   * @param {number} count - 需要检查的媒体数量，默认1
   * @returns {boolean} 前N个媒体是否已加载完毕
   */
  areFirstMediaLoaded(count = 1) {
    if (!this.albumMediaItems || this.albumMediaItems.length === 0) {
      return false;
    }
    
    const checkCount = Math.min(count, this.albumMediaItems.length);
    
    const isMediaReady = (mediaItem, index) => {
      if (isAliyunAlbumVideo(mediaItem)) {
        return true;
      }
      if (this.getAliyunDirectMediaUrl(mediaItem)) {
        return true;
      }
      if (mediaItem?.url && (mediaItem.url.startsWith('http://') || mediaItem.url.startsWith('https://'))) {
        return true;
      }
      return this.loadedMediaIndices.has(index);
    };

    // 🔥 修复：对于分享音乐，如果媒体项有直链URL，认为已加载
    if (this.currentSong && this.currentSong.isShareMusic) {
      for (let i = 0; i < checkCount; i++) {
        if (!isMediaReady(this.albumMediaItems[i], i)) {
          return false;
        }
      }
      return true;
    }
    
    // 预加载还没开始时，若首项可用阿里云/API 直链仍可展示
    if (this.loadedMediaIndices.size === 0) {
      for (let i = 0; i < checkCount; i++) {
        if (isMediaReady(this.albumMediaItems[i], i)) {
          continue;
        }
        return false;
      }
      return checkCount > 0;
    }
    
    for (let i = 0; i < checkCount; i++) {
      if (!isMediaReady(this.albumMediaItems[i], i)) {
        return false;
      }
    }
    
    return true;
  }

  /**
   * 从后端重新获取图集媒体列表（含 OneDrive / 阿里云 最新直链）
   */
  async fetchFreshAlbumMediaList() {
    const mediaAlbumId = this.currentSong?.mediaAlbumId;
    const fetchKey = this.currentSong?.isShareMusic
      ? `share-${this.currentSong.shareId}-${this.currentSong.id}`
      : `album-${mediaAlbumId}`;

    if (this._freshAlbumMediaListPromise && this._freshAlbumMediaListKey === fetchKey) {
      return this._freshAlbumMediaListPromise;
    }

    this._freshAlbumMediaListKey = fetchKey;
    this._freshAlbumMediaListPromise = (async () => {
      try {
        let response;
        if (this.currentSong?.isShareMusic) {
          const shareId = this.currentSong.shareId;
          const password = this.currentSong.sharePassword || '';
          const passwordParam = password ? `?password=${encodeURIComponent(password)}` : '';
          if (this.currentSong.isBatchShare) {
            response = await myRequest.get(
              `/api/music/batch-share/${shareId}/music/${this.currentSong.id}/album-media${passwordParam}`
            );
          } else {
            response = await myRequest.get(`/api/music/share/${shareId}/album-media${passwordParam}`);
          }
        } else if (mediaAlbumId) {
          response = await myRequest.get(`/api/media-album/${mediaAlbumId}/media`);
        } else {
          return [];
        }
        const items = Array.isArray(response) ? response : (response?.data ?? []);
        return prepareAlbumMediaItemsForPlayback(items);
      } catch (error) {
        console.warn('⚠️ [AlbumStrategy] 重新获取图集媒体直链失败:', error);
        return [];
      }
    })().finally(() => {
      this._freshAlbumMediaListPromise = null;
      this._freshAlbumMediaListKey = null;
    });

    return this._freshAlbumMediaListPromise;
  }

  async fetchFreshAlbumMediaUrlAtIndex(index) {
    const freshList = await this.fetchFreshAlbumMediaList();
    const freshItem = freshList[index];
    const freshUrl = freshItem?.url || freshItem?.pictureUrl || freshItem?.videoUrl || null;
    if (freshUrl && freshList[index]) {
      if (this.albumMediaItems[index] && freshList[index]) {
        this.albumMediaItems[index] = {
          ...this.albumMediaItems[index],
          ...freshList[index],
          url: freshUrl
        };
      }
    }
    return freshUrl;
  }

  /**
   * 缓存未命中时：用最新云盘直链下载并写入 IndexedDB，再返回 blob URL
   */
  async fetchCloudMediaIntoCache(mediaItem, index, userId, freshUrl) {
    if (!freshUrl || typeof freshUrl !== 'string') {
      return null;
    }
    try {
      console.log(`🖼️ [AlbumStrategy] 缓存未命中，从云盘拉取并写入缓存: 索引 ${index}`);
      await albumMediaCacheService.downloadAndCacheMedia(
        freshUrl,
        userId,
        this.currentSong.id,
        index
      );
      this.loadedMediaIndices.add(index);
      const blobUrl = await albumMediaCacheService.getMediaUrl(
        mediaItem,
        userId,
        this.currentSong.id,
        index
      );
      if (blobUrl?.startsWith('blob:')) {
        this.blobUrls.set(index, blobUrl);
        return blobUrl;
      }
    } catch (error) {
      console.warn(`⚠️ [AlbumStrategy] 云盘资源下载入缓存失败，索引 ${index}:`, error);
    }
    return null;
  }

  /**
   * 统一图集媒体 URL 策略：IndexedDB blob → 后端刷新云盘直链并下载入缓存 → 最后兜底
   */
  async resolvePlayableMediaUrl(mediaItem, index, userId) {
    if (isAliyunAlbumVideo(mediaItem)) {
      return buildAlbumHlsReadyUrl(mediaItem.videoId);
    }

    const cachedUrl = await albumMediaCacheService.getMediaUrl(
      mediaItem,
      userId,
      this.currentSong.id,
      index
    );
    if (cachedUrl?.startsWith('blob:')) {
      this.blobUrls.set(index, cachedUrl);
      return cachedUrl;
    }

    const freshUrl = await this.fetchFreshAlbumMediaUrlAtIndex(index);
    if (freshUrl) {
      const blobUrl = await this.fetchCloudMediaIntoCache(mediaItem, index, userId, freshUrl);
      if (blobUrl) {
        return blobUrl;
      }
      if (freshUrl.startsWith('http://') || freshUrl.startsWith('https://')) {
        console.log(`🔗 [AlbumStrategy] 使用后端刷新后的云盘直链（暂未入缓存）: 索引 ${index}`);
        return freshUrl;
      }
    }

    const aliyunDirect = this.getAliyunDirectMediaUrl(mediaItem);
    if (aliyunDirect) {
      console.warn(`⚠️ [AlbumStrategy] 云盘刷新失败，回退元数据中的阿里云直链（可能已过期）: 索引 ${index}`);
      return aliyunDirect;
    }

    if (this.currentSong?.isShareMusic && mediaItem?.url) {
      const directUrl = mediaItem.url;
      if (directUrl.startsWith('http://') || directUrl.startsWith('https://')) {
        return directUrl;
      }
    }

    return null;
  }
  
  /**
   * blob 解码失败时丢掉该项缓存，下次 getMediaUrl 会走云盘直链重下
   */
  async invalidateMediaCache(index) {
    if (!this.currentSong) return;
    let userId = this.getCurrentUserId();
    if (!userId && this.currentSong.isShareMusic && this.currentSong.shareId) {
      userId = `share_${this.currentSong.shareId}`;
    }
    if (!userId) return;

    const staleBlob = this.blobUrls.get(index);
    if (staleBlob) {
      try {
        URL.revokeObjectURL(staleBlob);
      } catch {
        /* ignore */
      }
      this.blobUrls.delete(index);
    }
    this.loadedMediaIndices.delete(index);
    this._mediaUrlInflight.delete(index);
    await albumMediaCacheService.invalidateCachedMedia(userId, this.currentSong.id, index);
  }

  /**
   * 获取媒体资源的URL（优先从缓存读取）
   * @param {Object} mediaItem - 媒体项
   * @param {number} index - 媒体在图集中的序号
   * @returns {Promise<string>} 媒体资源的URL
   */
  async getMediaUrl(mediaItem, index) {
    if (this._mediaUrlInflight.has(index)) {
      return this._mediaUrlInflight.get(index);
    }

    const request = this._resolveMediaUrl(mediaItem, index).finally(() => {
      this._mediaUrlInflight.delete(index);
    });
    this._mediaUrlInflight.set(index, request);
    return request;
  }

  async _resolveMediaUrl(mediaItem, index) {
    this.markFirstMediaUrlRequest(index, mediaItem?.mediaType);

    if (!this.currentSong) {
      // 🔥 修复：如果没有当前歌曲，返回 null，不返回原始 URL
      console.warn(`⚠️ [AlbumStrategy] 没有当前歌曲，无法获取媒体URL: 索引 ${index}`);
      this.markFirstMediaUrlNull(index, { reason: 'no-current-song' });
      return null;
    }
    
    // 🔥 修复：对于分享音乐，即使没有 userId，也使用 shareId 作为 userId 的替代
    let userId = this.getCurrentUserId();
    if (!userId && this.currentSong.isShareMusic) {
      const shareId = this.currentSong.shareId;
      if (shareId) {
        userId = `share_${shareId}`;
        console.log(`🖼️ [AlbumStrategy] 分享音乐场景，使用 shareId 作为 userId: ${userId}`);
      }
    }
    
    if (!userId) {
      // 🔥 修复：如果无法获取用户ID或分享ID，返回 null，不返回原始 URL
      console.warn(`⚠️ [AlbumStrategy] 无法获取用户ID或分享ID，无法获取媒体URL: 索引 ${index}`);
      this.markFirstMediaUrlNull(index, { reason: 'no-user-id' });
      return null;
    }
    
    try {
      const isLoaded = this.loadedMediaIndices.has(index);
      console.log(`🔍 [AlbumStrategy] 获取媒体URL: 索引 ${index}, 已加载: ${isLoaded}, 用户ID: ${userId}, 歌曲ID: ${this.currentSong.id}`);

      const url = await this.resolvePlayableMediaUrl(mediaItem, index, userId);

      if (url?.startsWith('blob:')) {
        console.log(`✅ [AlbumStrategy] 使用缓存的 Blob URL: 索引 ${index}`);
        this.markFirstMediaUrlReady(index, 'blob', { isLoaded });
        return url;
      }

      if (isAlbumHlsReadyUrl(url)) {
        this.markFirstMediaUrlReady(index, 'hls', { videoId: mediaItem?.videoId });
        return url;
      }

      if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
        const source = isAliyunCdnUrl(url) ? 'aliyun-direct-fresh' : 'cloud-direct-fresh';
        console.log(`✅ [AlbumStrategy] 使用云盘直链: 索引 ${index}, source=${source}`);
        this.markFirstMediaUrlReady(index, source, { isLoaded });
        return url;
      }

      console.warn(`⚠️ [AlbumStrategy] 获取媒体URL返回 null: 索引 ${index}, 已加载: ${isLoaded}`);
      this.markFirstMediaUrlNull(index, { reason: 'unresolved', isLoaded });
      return null;
    } catch (error) {
      console.error(`❌ [AlbumStrategy] 获取媒体URL失败: 索引 ${index}`, error);
      // 🔥 修复：出错时，对于分享音乐且有直链URL的情况，使用直链URL作为后备
      if (this.currentSong.isShareMusic && mediaItem && mediaItem.url) {
        const directUrl = mediaItem.url;
        if (directUrl && (directUrl.startsWith('http://') || directUrl.startsWith('https://'))) {
          console.log(`🔗 [AlbumStrategy] 获取缓存失败，使用直链URL作为后备: 索引 ${index}`);
          this.markFirstMediaUrlReady(index, 'direct-fallback', {
            message: error?.message || String(error)
          });
          return directUrl;
        }
      }
      // 🔥 修复：出错时返回 null，不返回原始 URL，避免触发 OneDrive 请求
      // 调用方应该处理 null 的情况，等待缓存或显示加载状态
      this.markFirstMediaUrlNull(index, {
        reason: 'error',
        message: error?.message || String(error)
      });
      return null;
    }
  }
  
  /**
   * 🔥 全屏相关方法
   */
  
  /**
   * 检查是否处于全屏状态
   * @returns {boolean}
   */
  isInFullscreen() {
    if (this.fullscreenControls && this.fullscreenControls.isInFullscreen) {
      return this.fullscreenControls.isInFullscreen('album');
    }
    return false;
  }
  
  /**
   * 同步是否处于图集全屏（由 DXHPlayer 在进入/退出全屏时调用）
   * @param {boolean} active
   */
  setAlbumFullscreenActive(active) {
    const next = Boolean(active);
    if (this.isAlbumFullscreen === next) {
      return;
    }
    this.isAlbumFullscreen = next;
    console.log('🖼️ [AlbumStrategy] 图集全屏状态:', next);
  }

  /**
   * 进入全屏（如果需要的话）
   * 图集版策略在播放时自动进入全屏模式
   * @returns {Promise<void>}
   */
  async enterFullscreenIfNeeded() {
    const controls = this.fullscreenControls;
    if (!controls?.enterFullscreen) {
      return;
    }

    // 播放页已打开、或带「音乐」标签且用户未明确要求自动全屏时，保持普通播放页
    const playerPageOpen = controls.isPlayerPageOpen?.() === true;
    const shouldAutoEnter = controls.shouldAutoEnterFullscreen?.() === true;
    if (songHasMusicTag(this.currentSong) && !shouldAutoEnter) {
      console.log('🖼️ [AlbumStrategy] 带音乐标签，跳过自动进入全屏');
      return;
    }
    if (playerPageOpen && !shouldAutoEnter) {
      console.log('🖼️ [AlbumStrategy] 播放页已打开，跳过自动进入全屏');
      return;
    }

    if (controls.isInFullscreen?.('album')) {
      return;
    }

    console.log('🖼️ [AlbumStrategy] 自动进入图集全屏模式');
    await controls.enterFullscreen('album');
  }
  
  /**
   * 退出全屏
   * @returns {Promise<void>}
   */
  async exitFullscreen() {
    if (this.fullscreenControls && this.fullscreenControls.exitFullscreen) {
      console.log('🖼️ [AlbumStrategy] 退出图集全屏模式');
      await this.fullscreenControls.exitFullscreen('album');
    }
  }
  
  /**
   * 获取当前媒体索引（供外部组件使用）
   * @returns {number} 当前媒体索引
   */
  getCurrentMediaIndex() {
    return this.currentMediaIndex;
  }
  
  /**
   * 获取当前媒体项（供外部组件使用）
   * @returns {Object|null} 当前媒体项
   */
  getCurrentMedia() {
    if (!this.albumMediaItems || this.albumMediaItems.length === 0) {
      return null;
    }
    return this.albumMediaItems[this.currentMediaIndex] || null;
  }
  
  /**
   * 清理资源
   */
  cleanup() {
    console.log('🧹 [AlbumStrategy] 开始清理资源');
    
    // 停止媒体切换
    this.stopMediaSwitching();
    
    // 🔥 修复：清理服务层的 Blob URL 缓存（避免内存泄漏）
    if (this.currentSong) {
      let cacheUserId = this.getCurrentUserId();
      // 🔥 修复：分享场景也需要清理缓存的 Blob URL，否则会复用已被 revoke 的 URL
      if (!cacheUserId && this.currentSong.isShareMusic && this.currentSong.shareId) {
        cacheUserId = `share_${this.currentSong.shareId}`;
      }
      if (cacheUserId) {
        albumMediaCacheService.clearBlobUrls(cacheUserId, this.currentSong.id);
      }
    }
    
    // 清理策略层的 Blob URL（虽然现在主要使用服务层缓存，但保留兼容性）
    this.blobUrls.forEach((url, index) => {
      try {
        URL.revokeObjectURL(url);
        console.log(`🧹 [AlbumStrategy] 清理Blob URL: 索引 ${index}`);
      } catch (error) {
        console.warn(`⚠️ [AlbumStrategy] 清理Blob URL失败: 索引 ${index}`, error);
      }
    });
    this.blobUrls.clear();
    
    // 清理 AudioController
    if (this.audioController) {
      // 停止高频率时间更新
      if (this.audioController.stopHighFreqTimeUpdate) {
        this.audioController.stopHighFreqTimeUpdate();
      }
      
      // 清理 AudioController
      this.audioController.cleanup();
      this.audioController = null;
    }
    
    // 重置图集相关状态
    this.albumMediaItems = [];
    this.currentMediaIndex = 0;
    this.currentSong = null;
    this.mediaSwitchingStarted = false;
    this._albumCompleted = false;
    this._mediaRoundComplete = false;
    this._audioEnded = false;
    this._awaitingAlbumComplete = false;
    this.isAlbumFullscreen = false;
    this._earlyPresentDone = false;
    this._earlyPresentPromise = null;
    this._mediaUrlInflight.clear();
    this._playInProgress = false;
    
    // 调用父类清理方法（清理事件监听器）
    super.cleanup();
    
    console.log('✅ [AlbumStrategy] 资源清理完成');
  }
}
